<template>
  <SettingsCard
    :title="$t('settings.features.ai-labeling.title')"
    :subtitle="$t('settings.features.ai-labeling.subtitle')"
    icon="mdi-robot"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="pt-4 px-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <span class="text-grey">
            {{ $t('settings.features.ai-labeling.body-text') }}
          </span>
        </v-col>

        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div class="w-100 mb-2">
            <span class="text-body-2">
              {{ $t('settings.features.ai-labeling.switch.label') }}
            </span>
          </div>
          <div class="d-flex flex-column ga-2">
            <v-switch
              v-model="markAiGeneratedTextContent"
              color="primary"
              hide-details
              density="compact"
              :loading="loading"
              :label="$t('settings.features.ai-labeling.switch.text-label')"
              @update:model-value="update"
            />

            <v-switch
              v-model="markAiGeneratedImageContent"
              color="primary"
              hide-details
              density="compact"
              :loading="loading"
              :label="$t('settings.features.ai-labeling.switch.image-label')"
              @update:model-value="update"
            />
          </div>
        </v-col>
      </v-row>
    </div>
  </SettingsCard>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import UPDATE_COMPANY_META_DATA from '@/modules/settings/companySettings/queries/UpdateCompanyMetaData.gql'

interface CompanyMeta {
  markAiGeneratedContent?: boolean
  markAiGeneratedTextContent?: boolean
  markAiGeneratedImageContent?: boolean
}

interface Company {
  meta?: CompanyMeta
}

const markAiGeneratedTextContent = ref(false)
const markAiGeneratedImageContent = ref(true)

const props = withDefaults(
  defineProps<{
    company?: Company
  }>(),
  { company: () => ({}) }
)

const { t } = useI18n()
const { isOpen, setOpen } = useQueryOpen({ settingName: 'ai-labeling' })
const { mutate } = useMutation(UPDATE_COMPANY_META_DATA)

const loading = ref(false)

watch(
  () => props.company,
  (val: Company | undefined) => {
    // legacy fallback for markAiGeneratedContent, if specific text/image labeling is not set
    const fallback = val?.meta?.markAiGeneratedContent ?? false

    markAiGeneratedTextContent.value = val?.meta?.markAiGeneratedTextContent ?? fallback

    // Default to true for image labeling if not set
    markAiGeneratedImageContent.value = val?.meta?.markAiGeneratedImageContent ?? true
  },
  { immediate: true }
)

async function update(): Promise<void> {
  try {
    loading.value = true
    await mutate({
      input: {
        markAiGeneratedTextContent: markAiGeneratedTextContent.value,
        markAiGeneratedImageContent: markAiGeneratedImageContent.value,
      },
    })
    showSnackbarMessage('success', t('alerts.settings.features.success'))
  } catch {
    showSnackbarMessage('error', t('alerts.settings.features.error'))
  } finally {
    loading.value = false
  }
}
</script>
